import { z } from 'zod'
import { STORE_PRODUCT_IDENTIFIERS } from './storeProducts'

/** Large enough for an aggregate App Store receipt while still bounding the native message. */
export const MAX_STORE_RECEIPT_CHARS = 700_000
export const MAX_STORE_TRANSACTIONS_PER_SETTLEMENT = 20

const MAX_STORE_DISPLAY_STRING_CHARS = 10_000

export const StoreProductIdentifierSchema = z.enum(STORE_PRODUCT_IDENTIFIERS)

export enum StoreErrorCode {
  PaymentCancelled = 2,
}

export const StoreProductsRequestSchema = z
  .object({
    productIdentifiers: z.array(StoreProductIdentifierSchema).min(1).max(STORE_PRODUCT_IDENTIFIERS.length),
  })
  .strict()

export const StoreProductSchema = z
  .object({
    productIdentifier: StoreProductIdentifierSchema,
    formattedPrice: z.string().max(MAX_STORE_DISPLAY_STRING_CHARS),
    currencyCode: z.string().max(100).nullable(),
    price: z.number().nullable(),
  })
  .strict()

export const StoreProductsSchema = z
  .object({
    products: z.array(StoreProductSchema).max(20),
  })
  .strict()

export const PurchaseStoreProductSchema = z
  .object({
    productIdentifier: StoreProductIdentifierSchema,
  })
  .strict()

export const StoreOperationReplySchema = z
  .object({
    status: z.enum(['queued', 'busy', 'unavailable']),
  })
  .strict()

export const StoreTransactionSchema = z
  .object({
    state: z.enum(['purchased', 'restored', 'failed', 'deferred']),
    productIdentifier: z.string().max(200).nullable(),
    transactionDate: z.string().max(200).nullable(),
    errorMessage: z.string().max(MAX_STORE_DISPLAY_STRING_CHARS).nullable(),
    errorCode: z.number().int().nullable(),
  })
  .strict()

export const StorePurchaseSettlementSchema = z
  .object({
    operation: z.enum(['purchase', 'restore']).nullable(),
    receipt: z.string().max(MAX_STORE_RECEIPT_CHARS).nullable(),
    transactions: z.array(StoreTransactionSchema).max(MAX_STORE_TRANSACTIONS_PER_SETTLEMENT),
  })
  .strict()

export const StorePurchaseSettlementReplySchema = z
  .object({
    canFinishTransaction: z.boolean(),
    isEntitlementGranted: z.boolean(),
  })
  .strict()

export type StoreProductsRequest = z.output<typeof StoreProductsRequestSchema>
export type StoreProduct = z.output<typeof StoreProductSchema>
export type StoreProducts = z.output<typeof StoreProductsSchema>
export type PurchaseStoreProduct = z.output<typeof PurchaseStoreProductSchema>
export type StoreOperationReply = z.output<typeof StoreOperationReplySchema>
export type StoreTransaction = z.output<typeof StoreTransactionSchema>
export type StorePurchaseSettlement = z.output<typeof StorePurchaseSettlementSchema>
export type StorePurchaseSettlementReply = z.output<typeof StorePurchaseSettlementReplySchema>
